import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { SummaryInfo } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseCategoryRequestModel } from "app/based/baseApiModel/baseCategory/BaseCategoryRequestModel";
import { BaseCategoryResponseModel } from "app/based/baseApiModel/baseCategory/BaseCategoryResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { NiceSimRegistrationRequestModel } from "features/niceSimRegistration/data/model/NiceSimRegistrationRequestModel";
import { NiceSimRegistrationResponseModel } from "features/niceSimRegistration/data/model/NiceSimRegistrationResponseModel";
import { NiceSimRegistrationInjection } from "features/niceSimRegistration/di/NiceSimRegistrationInjection";
import { NiceSimRegistrationUseCase } from "features/niceSimRegistration/domain/useCases/NiceSimRegistrationUseCase";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export interface IDropdownActionRecord {
  record: NiceSimRegistrationResponseModel;
  action: string;
}

export type SavedDetailActionType =
  | NiceSimRegistrationResponseModel
  | IDropdownActionRecord;
class NiceSimRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<NiceSimRegistrationRequestModel>(
    new NiceSimRegistrationRequestModel(),
  );
  public fetchNumberType = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchDestiny = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchNumerology = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchZodiac = new BehaviorSubject<BaseCategoryRequestModel>({});
  public exportExcel = new BehaviorSubject<NiceSimRegistrationRequestModel>(
    new NiceSimRegistrationRequestModel(),
  );
  public fetchDetail = new BehaviorSubject<NiceSimRegistrationRequestModel>(
    new NiceSimRegistrationRequestModel(),
  );
  public checkStatus = new BehaviorSubject<string>("");
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchNumberType.complete();
    this.fetchDestiny.complete();
    this.fetchNumerology.complete();
    this.fetchZodiac.complete();
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.checkStatus.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class NiceSimRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<NiceSimRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetail = new BehaviorSubject<SavedDetailActionType>({});
  public savedNumberType = new BehaviorSubject<
    Array<BaseCategoryResponseModel>
  >([]);
  public savedZodiac = new BehaviorSubject<Array<BaseCategoryResponseModel>>(
    [],
  );
  public savedDestiny = new BehaviorSubject<Array<BaseCategoryResponseModel>>(
    [],
  );
  public savedNumerology = new BehaviorSubject<
    Array<BaseCategoryResponseModel>
  >([]);
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public savedSummary = new BehaviorSubject<SummaryInfo>({});
  public savedPartners = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedStores = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedStaff = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedNumberType.complete();
    this.savedDestiny.complete();
    this.savedNumerology.complete();
    this.savedZodiac.complete();
    this.pagination.complete();
    this.savedSummary.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.savedPartners.complete();
    this.savedStores.complete();
    this.savedStaff.complete();
    this.savedUsers.complete();
  }
}

@injectable()
export class NiceSimRegistrationViewModel extends BaseViewModel<
  NiceSimRegistrationInput,
  NiceSimRegistrationOuput
> {
  private niceSimRegistrationUseCase: NiceSimRegistrationUseCase;
  constructor() {
    super(new NiceSimRegistrationInput(), new NiceSimRegistrationOuput());
    this.niceSimRegistrationUseCase =
      NiceSimRegistrationInjection.getNiceSimRegistrationUseCase();
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.niceSimRegistrationUseCase.getList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data ?? []);
          this.output.pagination.next(res?.pagination);
          this.output.savedSummary.next(res?.summary);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchNumberType, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getNiceSimCategory(
          params,
          "numberType",
        );
        if (res) {
          this.output.savedNumberType.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });
    this.skipFirstInitial(this.input.fetchDestiny, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getNiceSimCategory(
          params,
          "destiny",
        );
        if (res) {
          this.output.savedDestiny.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchNumerology, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getNiceSimCategory(
          params,
          "numerology",
        );
        if (res) {
          this.output.savedNumerology.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchZodiac, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getNiceSimCategory(
          params,
          "zodiac",
        );
        if (res) {
          this.output.savedZodiac.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getFilterList(
          params,
          "carrier",
        );
        if (res) {
          this.output.savedCarrier.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.niceSimRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.niceSimRegistrationUseCase.exportReport(params);
        if (res?.data) {
          if (res?.data?.size < 1000) {
            getMessage(strings.GlobalMessage.ExcelError, 3, "error");
            return;
          } else {
            const url = window.URL.createObjectURL(res?.data as Blob);
            const link = document.createElement("a");
            const timestamp = dayjs().format("YYYYMMDD_HHmmss");
            link.href = url;
            link.setAttribute("download", `NICE_SIM_REPORT_${timestamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            this.output.onExportSuccess.next(true);
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.input.statusLoading.next({ [code]: true });
        const res = await this.niceSimRegistrationUseCase.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.statusLoading.next({ [code]: false });
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);

        if (!isEmpty(cache)) {
          this.output.savedPartners.next(cache);
          return;
        }
        const res = await this.niceSimRegistrationUseCase.getFilterList(
          params,
          "partner",
        );
        if (res) {
          const data = res ?? [];
          this.output.savedPartners.next(data);
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.output.savedPartners.next([]);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      const { partnerCode } = params;
      try {
        if (partnerCode) {
          const res = await this.niceSimRegistrationUseCase.getFilterList(
            params,
            "store",
          );
          this.output.savedStores.next(res);
          return;
        }
      } catch (error) {
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.output.savedStaff.next([]);
        return;
      }
      try {
        const res = await this.niceSimRegistrationUseCase.getUserForFilter(
          params,
          "staff",
        );
        if (res) {
          const raw = res ?? [];
          const allUsers = raw.flatMap((item: any) => item?.users ?? []);
          const mappedUsers: BaseUserResponseModel[] = allUsers.map(
            (u: any) => {
              return {
                id: u.id,
                name: u.name || u.userName || u.email || u.phone || "",
                code: u.code,
              };
            },
          );
          this.output.savedStaff.next(mappedUsers);
        }
      } catch (e) {
        this.output.savedStaff.next([]);
        this.output.onError.next(e as Error);
      }
    });
    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      const { providerCode } = params;
      try {
        let res;
        if (providerCode === "b2b") {
          res = await this.niceSimRegistrationUseCase.getUserForFilter(
            params,
            "userB2B",
          );
        } else if (providerCode === "mobitopup" || providerCode === "smm") {
          res = await this.niceSimRegistrationUseCase.getUserForFilter(
            params,
            "staff",
          );
        } else {
          res = await this.niceSimRegistrationUseCase.getUserForFilter(
            params,
            "userB2B",
          );
        }
        const raw = res ?? [];
        const allUsers = raw.flatMap((item: any) => item?.users ?? []);
        const mappedUsers: BaseUserResponseModel[] = allUsers.map((u: any) => {
          return {
            id: u.id,
            name: u.fullName || u.email || u.name || u.phone || "",
            code: u.code,
          };
        });
        this.output.savedUsers.next(mappedUsers);
      } catch (e) {
        this.output.savedUsers.next([]);
        this.output.onError.next(e as Error);
      }
    });
  }
}
